import { useState } from 'react';
import type { HTMLAttributes } from 'react';
import { Autocomplete, Avatar, Box, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormDialog, useToast, withSelectedOption } from '../components';
import { ApiError } from '../api/problemDetails';
import { restartForIdentityChange, useWhoami } from '../api/whoami';
import { useStartImpersonation } from '../api/impersonation';
import { typeaheadPage, useTypeahead } from '../hooks/useTypeahead';
import { useUserOptions } from '../users/api/users';
import type { UserSummaryResponse } from '../users/types';

/** First-letter avatar seed, matching the account-menu / prototype treatment. */
function initialOf(user: UserSummaryResponse): string {
  return (user.fullName || user.email || '?').charAt(0).toUpperCase();
}

/**
 * Customer-portal Administrator "View as user" launcher (F4). Picks an active
 * user of the Administrator's **own customer** (`GET /users?active=true`, via the
 * shared `useUserOptions` picker read) and starts a real impersonation session
 * (`POST /me/impersonation/start`) with a **required, audited reason**. On
 * success it invalidates `['whoami']` and navigates to the portal root so the
 * foundation `AppShell` rebuilds as the target with the `ImpersonationBanner`
 * (which owns the exit). Own-customer scope is enforced server-side; the
 * own-customer picker keeps the 403 (`OutsideAdministratorScope`) unreachable,
 * and it is only reachable when not already impersonating (nested is blocked).
 *
 * Mounted only while open (the account menu conditionally renders it), so the
 * user fetch fires on open rather than on every shell mount.
 *
 * The picker **searches server-side** (BUG-034): the typed term is debounced into
 * the query, so a customer with more users than one page still resolves anyone by
 * name, email or employee number. It used to fetch 200 rows once and filter them
 * locally, which made everyone past row 200 unselectable.
 */
export function AdminImpersonationDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { data: whoami } = useWhoami();
  const customer = whoami?.customer?.displayName ?? whoami?.customer?.name ?? '';

  const [selected, setSelected] = useState<UserSummaryResponse | null>(null);
  const [reason, setReason] = useState('');

  const search = useTypeahead();
  const usersQuery = useUserOptions(search.searchTerm);
  const start = useStartImpersonation();

  // Exclude the signed-in Administrator — the backend rejects self-impersonation
  // (impersonation.self_target), so a self pick is a dead option (and would start
  // a nonsensical self-session in mock mode). `withSelectedOption` keeps the
  // current pick rendered once the term moves it off the fetched page.
  // Empty while the page on hand answers an older term (BUG-043).
  const page = typeaheadPage(search, usersQuery);
  const items = page.items;
  const options = withSelectedOption(
    items.filter((u) => u.id !== whoami?.effective?.userId),
    selected,
  );
  const total = page.total;
  const truncated = total > items.length;
  const canSubmit = !!selected && reason.trim().length > 0 && !start.isPending;

  const handleStart = async () => {
    if (!selected || reason.trim().length === 0) return;
    try {
      await start.mutateAsync({ targetUserId: selected.id, reason: reason.trim() });
      // Restart rather than reset the cache and navigate (BUG-046). The dialog is
      // mounted by the always-present shell, so an in-SPA transition leaves the
      // admin's own tree on screen under the impersonated identity, and every query
      // it owns 403s. A reload is the only point at which the two never coexist.
      //
      // No success toast: the reload discards it before it can render, and the
      // impersonation banner that comes back says the same thing and keeps saying it.
      restartForIdentityChange();
    } catch (err) {
      const detail = err instanceof ApiError ? err.problem.detail : undefined;
      toast.error(detail ?? t('errors.generic'));
    }
  };

  return (
    <FormDialog
      open
      onClose={onClose}
      title={t('impersonation.launcher.title')}
      subtitle={t('impersonation.launcher.subtitle')}
      onSubmit={handleStart}
      submitLabel={t('impersonation.launcher.start')}
      cancelLabel={t('actions.cancel')}
      submitPending={start.isPending}
      submitDisabled={!canSubmit}
    >
      <Autocomplete
        options={options}
        // Busy from the keystroke, not from the request: during the debounce
        // window the query isn't fetching and `keepPreviousData` is holding the
        // previous term's rows, so a picker keyed on `isFetching` alone shows
        // stale matches — or "No options" — as if that were the answer (BUG-042).
        loading={page.loading}
        loadingText={t('impersonation.launcher.loading')}
        value={selected}
        onChange={(_, next) => setSelected(next)}
        getOptionLabel={(u) => u.fullName}
        // The server matched this term against name, email and employee number
        // over the whole roster; re-filtering here would drop the hits whose
        // match isn't in the rendered label.
        // Nothing while awaiting, so MUI shows its loading text instead of the
        // previous term's rows; the selection stays in `options` for value matching.
        filterOptions={(opts) => (page.awaiting ? [] : opts)}
        inputValue={search.inputValue}
        onInputChange={search.onInputChange}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        noOptionsText={
          usersQuery.isError
            ? t('impersonation.launcher.loadError')
            : search.hasQuery
              ? t('userPicker.noMatch')
              : t('impersonation.launcher.empty')
        }
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label={t('impersonation.launcher.userLabel')}
            placeholder={t('impersonation.launcher.userPlaceholder')}
            helperText={
              truncated
                ? t('userPicker.truncated', { shown: items.length, total })
                : t('impersonation.launcher.userHint', { customer })
            }
            required
          />
        )}
        renderOption={(props, u) => {
          const { key, ...rest } = props as HTMLAttributes<HTMLLIElement> & { key?: string };
          return (
            <Box component="li" key={key} {...rest} sx={{ gap: 1.25 }}>
              <Avatar
                sx={{
                  width: 30,
                  height: 30,
                  bgcolor: 'primary.main',
                  color: '#fff',
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                {initialOf(u)}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3 }} noWrap>
                  {u.fullName}
                </Typography>
                <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.3 }} noWrap>
                  {u.email}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 11, color: 'text.secondary', flexShrink: 0 }}>
                {t(`userMenu.roles.${roleKey(u.primaryRole)}`)}
              </Typography>
            </Box>
          );
        }}
      />
      <TextField
        size="small"
        fullWidth
        multiline
        minRows={3}
        required
        label={t('impersonation.launcher.reasonLabel')}
        placeholder={t('impersonation.launcher.reasonPlaceholder')}
        helperText={t('impersonation.launcher.reasonHint')}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
    </FormDialog>
  );
}

/** Maps a `primaryRole` name to its `userMenu.roles.*` i18n key (camelCase). */
function roleKey(primaryRole: string): string {
  switch (primaryRole) {
    case 'Administrator':
      return 'administrator';
    case 'Manager':
      return 'manager';
    case 'RequestDelegate':
      return 'requestDelegate';
    default:
      return 'user';
  }
}

export default AdminImpersonationDialog;
